import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../api/axiosConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const { data } = await axios.get('/auth/me');
      setUser(data);
    } catch (error) {
      localStorage.removeItem('token');
      console.error('Failed to fetch user:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post('/auth/login', { email, password });
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const register = async (userData) => {
    const { data } = await axios.post('/auth/register', userData);
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data;
  };

  const updateProfile = async (profileData) => {
    const { data } = await axios.put('/auth/profile', profileData);
    setUser(prev => ({ ...prev, ...data }));
    return data;
  };

  const updateEmail = async (emailData) => {
    const { data } = await axios.put('/auth/email', emailData);
    setUser(prev => ({ ...prev, email: data.email }));
    return data;
  };

  const updatePassword = async (passwordData) => {
    const { data } = await axios.put('/auth/password', passwordData);
    return data;
  };

  const updateUserSettings = async (settings) => {
    const { data } = await axios.put('/auth/settings', settings);
    setUser(prev => ({ ...prev, settings: data.settings }));
    return data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      register,
      updateProfile,
      updateEmail,
      updatePassword,
      updateUserSettings
    }}>
      {children}
    </AuthContext.Provider>
  );
};